// The browser's own install dialog, where there is one (INSTALL.md §2, §4.3).
//
// Chromium raises `beforeinstallprompt` once the app is installable, usually
// soon after load and long before anybody opens the install page, so it is
// caught here at start-up and held. Holding it also keeps Chrome's own
// mini-infobar off a phone's first visit: the offer is ours, after a draft,
// never on arrival (§4.3). Safari and Firefox never raise it, so where it
// never came there is no button — the page shows that browser's steps.
//
// `appinstalled` says it worked, from either our button or the browser's own
// menu, and the account hears of it (`POST /api/me/install`) so the offer is
// not made again on another device.

import { useSyncExternalStore } from 'react';
import { reportInstall } from '../data/install';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let held: BeforeInstallPromptEvent | null = null;
let installedHere = false;
const listeners = new Set<() => void>();
const changed = () => {
  for (const l of listeners) l();
};

/** Called once, before the app renders. */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    held = e as BeforeInstallPromptEvent;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    held = null;
    installedHere = true;
    changed();
    void reportInstall('installed');
  });
}

interface PromptState {
  /** This browser will raise its install dialog from our button. */
  available: boolean;
  /** Installed from this browser during this visit. */
  installedHere: boolean;
}

let snapshot: PromptState = { available: false, installedHere: false };
function read(): PromptState {
  const available = held !== null;
  if (snapshot.available !== available || snapshot.installedHere !== installedHere) {
    snapshot = { available, installedHere };
  }
  return snapshot;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useInstallPrompt(): PromptState {
  return useSyncExternalStore(subscribe, read, read);
}

/** Raise the browser's dialog. A dialog can be raised once; a refusal ends it. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = held;
  if (event === null) return 'unavailable';
  held = null;
  changed();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}
